import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { MenuBadge } from 'app/components/MenuBadge';
import { Globals } from 'app/_globals';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import { environment } from 'environments/environment';
import { timer } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-site-tab-menu',
  template: `<app-card-tabs [backLink]="backLink" [tabitems]="tabitems"></app-card-tabs>`,
})
export class SiteTabMenuComponent implements OnInit, OnDestroy, OnChanges {
  @Input() site: any;
  @Input() optTitle: { from: string; to: string; };
  routeRole: any;
  backLink: any;
  tabitems: any[] = [];
  selectedSiteId: string;
  assetId: string;
  workOrderId: string;
  procedureId: string;

  private actionRequiredWorkOrderPollingSubscription: any;
  private workOrdersBadge: MenuBadge;
  private allowAssetScheduling = false;
  private allowIncidentReporting = false;

  constructor(
    public workOrderService: SiteWorkOrdersService,
    public globals: Globals,
    public activatedRoute: ActivatedRoute,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
      this.workOrderId = params.workOrderId;
      this.assetId = params.assetId;
      this.procedureId = params.procedureId;
    });

    this.routeRole = globals.user.routeRole;

    this.setTabItems();
  }

  ngOnInit() {
    if (environment.devModeDisableGlobalPolling) {
      return;
    }

    this.actionRequiredWorkOrderPollingSubscription = timer(0, 20000)
      .concatMap(() => {
        return this.workOrderService.getActionRequiredCountForSite(this.selectedSiteId);
      })
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((newCount) => {

        this.workOrdersBadge = {
          text: newCount.toString(),
          className: (newCount > 0) ? 'badge-danger' : 'badge-secondary',
          tooltip: newCount + ' require action',
        }

        this.setTabItems();
      });
  }

  ngOnDestroy(): void {
    if (this.actionRequiredWorkOrderPollingSubscription) {
      this.actionRequiredWorkOrderPollingSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.site && changes.site.currentValue) {
      this.allowAssetScheduling = this.site.siteOwner.siteOwnerUser.allowAssetScheduling;
      this.allowIncidentReporting  = this.site.siteOwner.siteOwnerUser.allowIncidentReporting;

      if (this.allowAssetScheduling || this.allowIncidentReporting) {
        this.setTabItems();
      }
    }
  }

  private setTabItems() {
    if (this.routeRole === 'power-station-manager') {
      this.tabitems = [
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/workers', title: 'Workers' },
      ];
    } else if (this.routeRole === 'power-station-owner') {
      this.tabitems = [
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/details', title: 'Details' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/workers', title: 'Workers' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/contact-information', title: 'Contact Info' },
      ];
    } else {
      this.backLink = { path: '/' + this.routeRole + '/sites/', title: 'All Sites' };

      this.tabitems = [
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/details', title: 'Details' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/location', title: 'Location' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets', title: 'Assets' },
        { subLink: true, path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/' + this.assetId, title: this.assetId },
        this.allowAssetScheduling && { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/schedule', title: 'Schedule' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/check-in', title: 'Check In' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/workers', title: 'Workers' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/work-orders', title: 'Work Orders', badge: this.workOrdersBadge },
        { subLink: true, path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/work-orders/' + this.workOrderId, title: this.workOrderId, badge: this.workOrdersBadge },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/site-sessions', title: 'Site Sessions' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/work-order-procedures', title: 'Procedures' },
        this.procedureId && { subLink: true, path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/work-order-procedures/view-procedure/' + this.procedureId, title: this.procedureId },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/site-competencies', title: 'Site Competencies' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/site-restrictions', title: 'Site Restrictions' },
        this.allowIncidentReporting && { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/site-incidents', title: 'Site Incidents' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/contact-information', title: 'Contact Info' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/documents', title: 'Documents' },
        { path: '/' + this.routeRole + '/sites/' + this.selectedSiteId + '/notes', title: 'Notes' },
      ];
    }

    // filter out undefined titles / sublinks. e.g. Work Order Sublink when not on the specific Work Order Page.
    this.tabitems = this.tabitems.filter(element => element && element.title);
    if (this.optTitle) {
      this.tabitems.forEach(tabItem => {
        if ('' + tabItem.title === '' + this.optTitle.from) {
          tabItem.title = this.optTitle.to;
        }
      });
    }
  }
}
